import React, { useState, useEffect } from "react";

const sections = [
  {
    id: "introduction",
    title: "Introduction",
    content: `Welcome to Causaler ("we", "our", or "us"). Your privacy is important to us. 
This Privacy Policy explains how we collect, use, and protect your information when you use our website or services.`,
  },
  {
    id: "information",
    title: "Information We Collect",
    content: `We may collect information such as your name, business name, email address, 
phone number, and project details when you fill out forms or contact us.`,
  },
  {
    id: "usage",
    title: "How We Use Information",
    content: `We use your information to respond to inquiries, provide services, 
communicate regarding projects, and improve our offerings. 
We do not sell or rent your personal information.`,
  },
  {
    id: "protection",
    title: "Data Protection",
    content: `We implement reasonable security measures to protect your data. 
However, no digital transmission method is completely secure.`,
  },
  {
    id: "thirdparty",
    title: "Third-Party Services",
    content: `We may use third-party tools for hosting, analytics, and communication. 
These providers may process limited technical data for functionality purposes.`,
  },
  {
    id: "cookies",
    title: "Cookies",
    content: `Our website may use cookies to enhance user experience and analyze website performance. 
You can disable cookies in your browser settings.`,
  },
  {
    id: "rights",
    title: "Your Rights",
    content: `You may request access, correction, or deletion of your data by contacting us at contact@causaler.com.`,
  },
  {
    id: "updates",
    title: "Policy Updates",
    content: `We may update this Privacy Policy from time to time. 
Changes will be reflected with a revised "Last Updated" date.`,
  },
  {
    id: "contact",
    title: "Contact Us",
    content: `Causaler  
India  
Email: contact@causaler.com`,
  },
];

const PrivacyPolicy = () => {
  const [activeTab, setActiveTab] = useState("introduction");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const activeContent = sections.find((sec) => sec.id === activeTab);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.updated}>Last Updated: February 21, 2026</p>

        {!isMobile ? (
          // ================= DESKTOP VIEW =================
          <div style={styles.contentWrapper}>
            <div style={styles.sidebar}>
              {sections.map((section) => (
                <div
                  key={section.id}
                  onClick={() => setActiveTab(section.id)}
                  style={{
                    ...styles.tab,
                    backgroundColor:
                      activeTab === section.id ? "#ff7a00" : "#0b1d3a",
                    color: "#ffffff",
                  }}
                >
                  {section.title}
                </div>
              ))}
            </div>

            <div style={styles.contentArea}>
              <h2 style={styles.sectionTitle}>{activeContent.title}</h2>
              <p style={styles.text}>{activeContent.content}</p>
            </div>
          </div>
        ) : (
          // ================= MOBILE VIEW =================
          <div>
            {sections.map((section) => (
              <div key={section.id} style={styles.mobileSection}>
                <div
                  onClick={() =>
                    setActiveTab(
                      activeTab === section.id ? null : section.id
                    )
                  }
                  style={{
                    ...styles.mobileTab,
                    backgroundColor:
                      activeTab === section.id ? "#ff7a00" : "#0b1d3a",
                  }}
                >
                  {section.title}
                </div>

                {activeTab === section.id && (
                  <div style={styles.mobileContent}>
                    <p style={styles.text}>{section.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={styles.footer}>
          Causaler - Helping Small Businesses Go Digital
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "linear-gradient(135deg, #0b1d3a 0%, #112b5c 100%)",
    minHeight: "100vh",
    padding: "60px 20px",
  },
  container: {
    maxWidth: "1100px",
    margin: "0 auto",
    backgroundColor: "#ffffff",
    borderRadius: "20px",
    padding: "40px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "10px",
    color: "#0b1d3a",
    fontFamily: "'Science Gothic', sans-serif", // Heading font
  },
  updated: {
    textAlign: "center",
    color: "#777",
    marginBottom: "40px",
    fontSize: "14px",
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  contentWrapper: {
    display: "flex",
    gap: "30px",
  },
  sidebar: {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  tab: {
    padding: "14px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s ease",
    fontFamily: "'Science Gothic', sans-serif", // Tab headings
  },
  contentArea: {
    flex: "2",
    backgroundColor: "#f8fafc",
    padding: "30px",
    borderRadius: "14px",
  },
  sectionTitle: {
    fontSize: "22px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#0b1d3a",
    fontFamily: "'Science Gothic', sans-serif", // Section headings
  },
  text: {
    fontSize: "15px",
    lineHeight: "1.8",
    color: "#444",
    whiteSpace: "pre-line",
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", // Body font
  },
  mobileSection: {
    marginBottom: "12px",
  },
  mobileTab: {
    padding: "14px",
    borderRadius: "10px",
    color: "#fff",
    fontWeight: "500",
    cursor: "pointer",
    fontFamily: "'Science Gothic', sans-serif", // Mobile tab headings
  },
  mobileContent: {
    backgroundColor: "#f8fafc",
    padding: "20px",
    borderRadius: "10px",
  },
  footer: {
    marginTop: "50px",
    textAlign: "center",
    fontSize: "14px",
    color: "#666",
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
};

export default PrivacyPolicy;